import { cn, useDebounceByHash } from "@/lib/utils"
import { toast } from "sonner"
import { V2MarketType, V2ParameterSection, V2SectionOrder, V2SheetStatus } from "@/packaging-v2/lib/schema"
import {
  V2FormSchemaBuilder,
  BuildSectionsOptions,
  isFormComplete,
} from "@/packaging-v2/lib/form"
import ItemHeader from "@/packaging-v2/components/item-header"
import SectionForm from "@/packaging-v2/components/section-form"
import ParamRemarksSheet from "@/packaging-v2/components/param-remarks-sheet"
import Button from "@/components/button"
import useStore from "@/store"
import { FormParamStatus } from "@/lib/form/types"
import { deleteV2Item, submitV2Sheets } from "@/services/packaging-v2"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ConfirmActionSheet from "@/components/confirm-action-sheet"
import { HTTPError } from "ky"


export interface PackagingV2FormProps {
  className?: string
  canEdit?: boolean
}

/**
 * Main form component for Packaging V2
 * Orchestrates ItemHeader and SectionForm components
 */
// Type for tracking which param/window needs a remark
interface RemarkTarget {
  sectionType: V2ParameterSection
  paramId: string
  windowIndex?: number
}

export default function PackagingV2Form({
  className,
  canEdit = true,
}: PackagingV2FormProps) {
  const packagingV2Store = useStore.use.packagingV2()
  const sessionStore = useStore.use.session()

  const { currDeptId, currMachineId, currItemIndex, forms } = packagingV2Store

  // Debounce for syncing form to store
  const debounce = useDebounceByHash(500)

  // State for remarks sheet
  const [remarksOpen, setRemarksOpen] = useState(false)
  const [remarkTarget, setRemarkTarget] = useState<RemarkTarget | null>(null)

  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingItemKey, setDeletingItemKey] = useState<string | null>(null)

  // Get current form data
  const currentForm = useMemo(() => {
    return forms.find(
      (f) => f.deptId === currDeptId && f.id === currMachineId
    )
  }, [currDeptId, currMachineId, forms])

  // Get current item
  const currentItem = useMemo(() => {
    return currentForm?.items[currItemIndex]
  }, [currentForm, currItemIndex])

  const currentItemDeleteKey = useMemo(() => {
    if (!currentItem) return null
    return `${currDeptId}.${currMachineId}.${currentItem.sequenceOrder}`
  }, [currentItem, currDeptId, currMachineId])

  const isDeletingCurrentItem = !!currentItemDeleteKey && deletingItemKey === currentItemDeleteKey

  // Get department schema for products
  const deptSchema = useMemo(() => {
    return packagingV2Store.getDeptSchema(currDeptId)
  }, [currDeptId, packagingV2Store.allDepartments])

  // Check if current item has a pending submission
  const isSubmitting = useMemo(() => {
    return packagingV2Store.submissions.some(
      (s) =>
        s.deptId === currDeptId &&
        s.machineId === currMachineId &&
        s.itemIndex === currItemIndex
    )
  }, [packagingV2Store.submissions, currDeptId, currMachineId, currItemIndex])

  // Get products for current machine
  const products = useMemo(() => {
    return packagingV2Store.getProductsForMachine(currMachineId)
  }, [currMachineId, packagingV2Store.allDepartments, packagingV2Store.allProducts])

  // Build options for form schema builder
  const buildOptions: BuildSectionsOptions = useMemo(() => ({
    shiftStartedAt: sessionStore.shift.startedAt,
    shiftEndedAt: sessionStore.shift.endedAt,
  }), [sessionStore.shift])

  // Track if DRAFT sync is in progress
  const isDraftSyncing = useRef(false)

  // Sync DRAFT data to API (lenient - silent errors)
  const syncDraftToApi = useCallback(async () => {
    // Skip if already syncing or item is already submitted
    if (isDraftSyncing.current || !currentItem || currentItem.status === FormParamStatus.SUBMITTED) {
      return
    }

    // Update last edit time
    sessionStore.setProcessUpdatedAt(
      packagingV2Store.shift.processId,
      new Date().toISOString()
    )

    // Build payload with DRAFT status
    const payload = packagingV2Store.buildSubmissionPayload(
      currDeptId,
      currMachineId,
      V2SheetStatus.DRAFT
    )

    if (!payload || payload.length === 0) {
      return
    }

    try {
      isDraftSyncing.current = true
      await submitV2Sheets(payload)
    } catch (error) {
      // Silent failure for DRAFT sync - no toast
      console.error("DRAFT sync error:", error)
    } finally {
      isDraftSyncing.current = false
    }
  }, [currentItem, currDeptId, currMachineId, packagingV2Store, sessionStore])

  // Create form schema builder
  const builder = useMemo(() => {
    if (!deptSchema) return null
    const formSchema = packagingV2Store.formSchemas.find(
      (f) => f.id === currDeptId
    )
    return new V2FormSchemaBuilder(deptSchema, packagingV2Store.allProducts, formSchema)
  }, [deptSchema, currDeptId, packagingV2Store.formSchemas, packagingV2Store.allProducts])

  // Effect to rebuild sections from schema for hydrated items with incomplete sections
  useEffect(() => {
    if (!builder || !currentItem || !currentItem.productId || !currentItem.skuId) return

    // Check if sections are incomplete (hydrated from pending sheets only has saved readings)
    const expectedSections = V2SectionOrder.length
    const actualSections = currentItem.sections.length

    if (actualSections >= expectedSections) return // Sections already complete

    // Find the SKU to rebuild sections
    const product = products.find((p) => p.id === currentItem.productId)
    const sku = product?.skus.find((s) => s.id === currentItem.skuId)

    if (!sku) return

    // Build full sections from schema
    const fullSections = builder.buildAllSections(sku, currentItem.marketType, buildOptions)

    // Merge existing values from hydrated sections into full sections
    for (const existingSection of currentItem.sections) {
      const targetSection = fullSections.find((s) => s.type === existingSection.type)
      if (targetSection) {
        // Preserve status from existing section
        targetSection.status = existingSection.status
        // Merge parameter values
        for (const existingParam of existingSection.parameters) {
          const targetParam = targetSection.parameters.find((p) => p.id === existingParam.id)
          if (targetParam) {
            targetParam.value = existingParam.value
            targetParam.remark = existingParam.remark
            targetParam.recordedAt = existingParam.recordedAt
            targetParam.status = existingParam.status
            // Merge window values
            if (existingParam.windows && targetParam.windows) {
              for (let i = 0; i < targetParam.windows.length; i++) {
                const existingWindow = existingParam.windows.find(
                  (w) => w.startTime === targetParam.windows![i].startTime
                )
                if (existingWindow) {
                  targetParam.windows[i].value = existingWindow.value
                  targetParam.windows[i].recordedAt = existingWindow.recordedAt
                  targetParam.windows[i].remark = existingWindow.remark
                }
              }
            }
          }
        }
      }
    }

    // Update item with full sections
    packagingV2Store.setItemFormData(currDeptId, currMachineId, currItemIndex, {
      sections: fullSections,
    })
  }, [builder, currentItem, products, buildOptions, currDeptId, currMachineId, currItemIndex, packagingV2Store])

  // Get INFORMATION_FIELDS section for vendor and other info fields
  const infoFieldsSection = useMemo(() => {
    return currentItem?.sections.find(
      (s) => s.type === V2ParameterSection.INFORMATION_FIELDS
    )
  }, [currentItem])

  // Handle market type change
  const handleMarketTypeChange = useCallback((marketType: V2MarketType) => {
    if (!builder || !currentItem) return

    // If SKU is already selected, rebuild sections with new market type
    if (currentItem.productId && currentItem.skuId) {
      const product = products.find((p) => p.id === currentItem.productId)
      const sku = product?.skus.find((s) => s.id === currentItem.skuId)

      if (sku) {
        const sections = builder.buildAllSections(sku, marketType, buildOptions)
        packagingV2Store.setItemFormData(currDeptId, currMachineId, currItemIndex, {
          marketType,
          sections,
        })
        return
      }
    }

    // Just update market type if no SKU selected
    packagingV2Store.setItemFormData(currDeptId, currMachineId, currItemIndex, {
      marketType,
    })
  }, [builder, currentItem, currMachineId, currItemIndex, currDeptId, buildOptions, packagingV2Store, products])

  // Handle product change
  const handleProductChange = useCallback((productId: string) => {
    if (!builder || !currentItem) return

    // Clear SKU when product changes
    packagingV2Store.setItemFormData(currDeptId, currMachineId, currItemIndex, {
      productId,
      productName: products.find((p) => p.id === productId)?.name,
      skuId: undefined,
      skuName: undefined,
      sections: [], // Clear sections until SKU is selected
    })
  }, [builder, currentItem, currDeptId, currMachineId, currItemIndex, products, packagingV2Store])

  // Handle SKU change
  const handleSKUChange = useCallback((skuId: string) => {
    if (!builder || !currentItem || !currentItem.productId) return

    const product = products.find((p) => p.id === currentItem.productId)
    const sku = product?.skus.find((s) => s.id === skuId)

    if (!sku) return

    // Build sections directly from SKU parameters
    const sections = builder.buildAllSections(sku, currentItem.marketType, buildOptions)

    // Update item form data with SKU and sections
    packagingV2Store.setItemFormData(currDeptId, currMachineId, currItemIndex, {
      skuId,
      skuName: sku.name,
      sections,
    })
  }, [builder, currentItem, currDeptId, currMachineId, currItemIndex, products, buildOptions, packagingV2Store])

  // Handle param value change
  const handleParamChange = useCallback((
    sectionType: V2ParameterSection,
    paramId: string,
    value: string
  ) => {
    packagingV2Store.setParamFormData(
      currDeptId,
      currMachineId,
      currItemIndex,
      sectionType,
      paramId,
      {
        value,
        recordedAt: new Date().toISOString(),
        status: FormParamStatus.DRAFT,
      }
    )
    debounce("syncDraftToApi", syncDraftToApi, 5000) // Lenient 5s debounce for DRAFT sync
  }, [currDeptId, currMachineId, currItemIndex, packagingV2Store, debounce, syncDraftToApi])

  // Handle info field (vendor, etc.) value change
  const handleInfoFieldChange = useCallback((
    paramId: string,
    value: string
  ) => {
    packagingV2Store.setParamFormData(
      currDeptId,
      currMachineId,
      currItemIndex,
      V2ParameterSection.INFORMATION_FIELDS,
      paramId,
      {
        value,
        recordedAt: new Date().toISOString(),
        status: FormParamStatus.DRAFT,
      }
    )
    debounce("syncDraftToApi", syncDraftToApi, 5000) // Lenient 5s debounce for DRAFT sync
  }, [currDeptId, currMachineId, currItemIndex, packagingV2Store, debounce, syncDraftToApi])

  // Handle window value change
  const handleWindowChange = useCallback((
    sectionType: V2ParameterSection,
    paramId: string,
    windowIndex: number,
    value: string
  ) => {
    if (!currentItem) return

    const section = currentItem.sections.find((s) => s.type === sectionType)
    const param = section?.parameters.find((p) => p.id === paramId)

    if (!param || !param.windows) return

    const updatedWindows = [...param.windows]
    updatedWindows[windowIndex] = {
      ...updatedWindows[windowIndex],
      value,
      recordedAt: new Date().toISOString(),
    }

    packagingV2Store.setParamFormData(
      currDeptId,
      currMachineId,
      currItemIndex,
      sectionType,
      paramId,
      {
        windows: updatedWindows,
        status: FormParamStatus.DRAFT,
      }
    )
    debounce("syncDraftToApi", syncDraftToApi, 5000) // Lenient 5s debounce for DRAFT sync
  }, [currentItem, currDeptId, currMachineId, currItemIndex, packagingV2Store, debounce, syncDraftToApi])

  // Handle section toggle expand
  const handleToggleExpand = useCallback((sectionType: V2ParameterSection) => {
    if (!currentItem) return

    const section = currentItem.sections.find((s) => s.type === sectionType)
    if (!section) return

    packagingV2Store.setSectionFormData(
      currDeptId,
      currMachineId,
      currItemIndex,
      sectionType,
      { isExpanded: !section.isExpanded }
    )
  }, [currentItem, currDeptId, currMachineId, currItemIndex, packagingV2Store])

  // Handle confirm all sections
  const handleConfirmAll = useCallback(() => {
    // Prevent submission if item doesn't exist or is already submitted
    if (!currentItem || currentItem.status === FormParamStatus.SUBMITTED) return

    // Update last edit time for the process
    sessionStore.setProcessUpdatedAt(
      packagingV2Store.shift.processId,
      new Date().toISOString()
    )

    // Add to submission queue - status will be updated after API success
    packagingV2Store.addSubmission({
      deptName: deptSchema?.name || "",
      machineName: currentForm?.name || "",
      deptId: currDeptId,
      machineId: currMachineId,
      itemIndex: currItemIndex,
    })

    toast.info("Submitting readings...")
  }, [currentItem, currDeptId, currMachineId, currItemIndex, deptSchema, currentForm, packagingV2Store, sessionStore])

  // Handle error flag click - open remarks sheet
  const handleClickErrorFlag = useCallback((
    sectionType: V2ParameterSection,
    paramId: string,
    windowIndex?: number
  ) => {
    setRemarkTarget({ sectionType, paramId, windowIndex })
    setRemarksOpen(true)
  }, [])

  // Get initial remark for the remarks sheet
  const getInitialRemark = useCallback(() => {
    if (!remarkTarget || !currentItem) return ""

    const section = currentItem.sections.find((s) => s.type === remarkTarget.sectionType)
    const param = section?.parameters.find((p) => p.id === remarkTarget.paramId)

    if (!param) return ""

    if (remarkTarget.windowIndex !== undefined && param.windows) {
      return param.windows[remarkTarget.windowIndex]?.remark || ""
    }

    return param.remark || ""
  }, [remarkTarget, currentItem])

  // Handle saving remark
  const handleSaveRemark = useCallback((remark: string) => {
    if (!remarkTarget) return

    if (remarkTarget.windowIndex !== undefined) {
      // Window-level remark
      packagingV2Store.setWindowRemark(
        currDeptId,
        currMachineId,
        currItemIndex,
        remarkTarget.sectionType,
        remarkTarget.paramId,
        remarkTarget.windowIndex,
        remark
      )
    } else {
      // Param-level remark
      packagingV2Store.setParamRemark(
        currDeptId,
        currMachineId,
        currItemIndex,
        remarkTarget.sectionType,
        remarkTarget.paramId,
        remark
      )
    }

    setRemarksOpen(false)
    setRemarkTarget(null)
  }, [remarkTarget, currDeptId, currMachineId, currItemIndex, packagingV2Store])

  /**
   * Handles the delete button click - shows confirmation dialog
   * Prevents deletion of submitted items
   */
  const handleDeleteClick = useCallback(() => {
    if (!currentItem) return
    if (isDeletingCurrentItem) return

    // Don't allow deleting submitted items
    if (currentItem.status === FormParamStatus.SUBMITTED) {
      toast.error("Cannot delete a submitted item")
      return
    }

    // Backend delete is item-scoped and requires persisted item keys
    if (!currentItem.productId || !currentItem.skuId) {
      toast.error("Select product and SKU before deleting this item")
      return
    }

    setShowDeleteConfirm(true)
  }, [currentItem, isDeletingCurrentItem])

  /**
   * Handles canceling the delete operation
   */
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  /**
   * Handles the deletion of an item from the packaging form
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!currentItem || !currentItemDeleteKey || isDeletingCurrentItem) {
      return
    }

    if (!currentItem.productId || !currentItem.skuId) {
      toast.error("Select product and SKU before deleting this item")
      return
    }

    setDeletingItemKey(currentItemDeleteKey)
    try {
      await deleteV2Item({
        departmentId: currDeptId,
        machineId: currMachineId,
        productId: currentItem.productId,
        skuId: currentItem.skuId,
        sequenceOrder: currentItem.sequenceOrder,
      })

      packagingV2Store.removeItem(currDeptId, currMachineId, currItemIndex)

      toast.success("Item deleted successfully")
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error("Error deleting item:", error)

      if (error instanceof HTTPError) {
        const isValidationError = error.response.status === 400
        let backendCode = ""

        try {
          const body = await error.response.clone().json() as Record<string, unknown>
          const rawCode = body?.error_code || body?.code
          backendCode = typeof rawCode === "string" ? rawCode : ""
        } catch {
          backendCode = ""
        }

        if (isValidationError) {
          const isPackaging027 =
            backendCode === "PACKAGING_027" ||
            error.message.includes("PACKAGING_027") ||
            backendCode === ""

          if (isPackaging027) {
            toast.error("Unable to delete this item because its details are invalid (PACKAGING_027).")
            return
          }

          toast.error(error.message || "Unable to delete this item due to a validation error.")
          return
        }

        toast.error(error.message || "Failed to delete item. Please try again.")
        return
      }

      toast.error("Failed to delete item. Please try again.")
    } finally {
      setDeletingItemKey(null)
    }
  }, [
    currentItem,
    currentItemDeleteKey,
    isDeletingCurrentItem,
    currDeptId,
    currMachineId,
    currItemIndex,
    packagingV2Store,
  ])

  // Show empty state if no form data
  if (!currentForm || !currentItem) {
    return (
      <div className={cn("text-center py-8 text-gray-500", className)}>
        <p>No item selected</p>
        <p className="text-sm mt-2">Add an item to start filling the form</p>
      </div>
    )
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Item Header */}
      <ItemHeader
        item={currentItem}
        products={products}
        infoFieldsSection={infoFieldsSection}
        disabled={!canEdit}
        canDelete={
          canEdit &&
          currentItem.status !== FormParamStatus.SUBMITTED &&
          !!currentItem.productId &&
          !!currentItem.skuId
        }
        isDeleteLoading={isDeletingCurrentItem}
        onMarketTypeChange={handleMarketTypeChange}
        onProductChange={handleProductChange}
        onSKUChange={handleSKUChange}

        onInfoFieldChange={handleInfoFieldChange}
        onDelete={handleDeleteClick}
      />

      {/* Section Forms */}
      {currentItem.sections.length > 0 ? (
        <div className="space-y-4">
          {V2SectionOrder.filter(
            (sectionType) => sectionType !== V2ParameterSection.INFORMATION_FIELDS
          ).map((sectionType) => {
            const section = currentItem.sections.find(
              (s) => s.type === sectionType
            )
            if (!section) return null

            return (
              <SectionForm
                key={`${currMachineId}-${currentItem.id}-${sectionType}`}
                section={section}
                disabled={!canEdit}
                onParamChange={(paramId, value) =>
                  handleParamChange(sectionType, paramId, value)
                }
                onWindowChange={(paramId, windowIndex, value) =>
                  handleWindowChange(sectionType, paramId, windowIndex, value)
                }
                onToggleExpand={() => handleToggleExpand(sectionType)}
                onClickErrorFlag={(paramId, windowIndex) =>
                  handleClickErrorFlag(sectionType, paramId, windowIndex)
                }
              />
            )
          })}

          {/* Submit Button */}
          {currentItem.status !== FormParamStatus.SUBMITTED && canEdit && (
            <div className="pt-4">
              <Button
                className="w-full"
                disabled={!isFormComplete(currentItem) || isSubmitting}
                onClick={handleConfirmAll}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 bg-white rounded-lg border">
          <p>Select a product and SKU to load parameters</p>
        </div>
      )}

      {/* Remarks Sheet */}
      <ParamRemarksSheet
        open={remarksOpen}
        initialRemark={getInitialRemark()}
        onOpenChange={setRemarksOpen}
        onSave={handleSaveRemark}
      />

      {/* Delete Confirmation Sheet */}
      <ConfirmActionSheet
        open={showDeleteConfirm}
        title="Delete this item?"
        description="This will remove this item and its readings."
        confirmLabel="Delete"
        loading={isDeletingCurrentItem}
        onClose={handleCancelDelete}
        onClickBack={handleCancelDelete}
        onClickConfirm={handleConfirmDelete}
      />
    </div>
  )
}
